import _ from 'lodash';
import { createSelector } from 'reselect';

import { StoreState } from '../../redux/reducers/store.types';

import {
  TeamMemberFormAccessLevel,
  TeamMemberFormAccessPermissions,
  TeamMemberRoles,
} from './team.constants';
import { TeamUiState } from './team.reducer-ui';
import {
  TeamMember,
  TeamMemberAccess,
  TeamMemberSecurityGroup,
} from './team.types';

const teamUiState = (state: StoreState): TeamUiState => state.team.teamUiState;

export const isAddTeamMemberModalOpened = createSelector(
  teamUiState,
  (state: TeamUiState): boolean => state.isAddTeamMemberModalOpened,
);

export const isTeamMemberDeleteModalOpened = createSelector(
  teamUiState,
  (state: TeamUiState): boolean => state.isTeamMemberDeleteModalOpened,
);

export const isTeamMemberEditModalOpened = createSelector(
  teamUiState,
  (state: TeamUiState): boolean => state.isTeamMemberEditModalOpened,
);

export const getTeamMembersSelectedProjectId = createSelector(
  teamUiState,
  (state: TeamUiState): string => state.teamMembersSelectedProjectId,
);

export const getTeamMembersSelectedRole = createSelector(
  teamUiState,
  (state: TeamUiState): TeamMemberRoles => state.teamMembersSelectedRole,
);

export const getDeletingTeamMember = createSelector(
  teamUiState,
  (state: TeamUiState): TeamMember | undefined => state.deletingTeamMember,
);

export const getDeletingTeamMemberId = createSelector(
  teamUiState,
  (state: TeamUiState): string | undefined =>
    state.deletingTeamMember ? state.deletingTeamMember.id : undefined,
);

export const getEditingTeamMember = createSelector(
  teamUiState,
  (state: TeamUiState): TeamMember | undefined => state.editingTeamMember,
);

export const getEditingTeamMemberAvatarUrl = createSelector(
  getEditingTeamMember,
  (editingTeamMember: TeamMember | undefined): string =>
    _.get(editingTeamMember, 'avatarUrl', ''),
);

export const getEditingTeamMemberEmail = createSelector(
  getEditingTeamMember,
  (editingTeamMember: TeamMember | undefined): string =>
    _.get(editingTeamMember, 'primaryEmail.address', ''),
);

export const getEditingTeamMemberId = createSelector(
  getEditingTeamMember,
  (editingTeamMember: TeamMember | undefined): string =>
    _.get(editingTeamMember, 'id', ''),
);

export const getEditingTeamMemberName = createSelector(
  getEditingTeamMember,
  (editingTeamMember: TeamMember | undefined): string =>
    _.get(editingTeamMember, 'name', ''),
);

export const getTeamMembersSearchValue = createSelector(
  teamUiState,
  (state: TeamUiState): string => state.teamMembersSearchValue,
);

const isTeamMemberFormAccessPermission = (
  permission,
): permission is TeamMemberFormAccessPermissions =>
  Object.values(TeamMemberFormAccessPermissions).includes(permission);

export const getSecurityGroupsByMember = (
  member: TeamMember,
): TeamMemberAccess[] => {
  const securityGroups = _.reduce(
    member.securityGroups,
    (acc: TeamMemberAccess[], securityGroup: TeamMemberSecurityGroup) => {
      if (!isTeamMemberFormAccessPermission(securityGroup.permission)) {
        return acc;
      }

      acc.push({
        level: securityGroup.projectId
          ? TeamMemberFormAccessLevel.Project
          : TeamMemberFormAccessLevel.Organization,
        permissions: securityGroup.permission,
        projectsIds: securityGroup.projectId ? [securityGroup.projectId] : [],
      });

      return acc;
    },
    [],
  );

  if (!_.isEmpty(securityGroups)) {
    return securityGroups;
  }

  return [
    {
      level: undefined,
      permissions: TeamMemberFormAccessPermissions.Viewer,
      projectsIds: [],
    },
  ];
};

export const getEditingTeamMemberSecurityGroups = createSelector(
  getEditingTeamMember,
  getSecurityGroupsByMember,
);
